// One material family for the whole game: flat-shaded, vertex-coloured toon with hard bands,
// and the golden haze computed inside the material (distance + height, warmer toward the sun).
// See ART_DIRECTION.md §3. Every lane builds its meshes from these factories.

import * as THREE from 'three';
import { PAL } from './palette';

/** Shared haze uniforms; mutate .value to restyle the whole world in one place. */
export const HAZE = {
  uHazeColor: { value: new THREE.Color(0xf8dba6) },
  uSunHazeColor: { value: new THREE.Color(PAL.sunCore) },
  uLowHazeColor: { value: new THREE.Color(0xf8e2b8) },
  uSunDir: { value: new THREE.Vector3(-0.55, 0.42, -0.72).normalize() },
  uHazeDensity: { value: 0.0078 },
  uHazeBase: { value: -6.0 },
  uHazeHeightFalloff: { value: 0.045 },
  uHazeLow: { value: 0.75 },
  uTime: { value: 0 },
  /** 0 meadow (golden haze) .. 1 town (blue sky, thin haze, neutral grade). Driven by the
   *  world view from the camera position; the sky dome and the post grade read it directly. */
  uTownMood: { value: 0 },
  /** strength of the slow drifting cloud shadows on the meadow (grass + painted ground) */
  uCloudShadow: { value: 0.56 },
  /** the local cowboy's chest (game.ts); foliage between it and the camera dithers out */
  uFocus: { value: new THREE.Vector3(0, -1e5, 0) },
  /** 0 day .. 1 night (world/daynight.ts): the golden ground mist turns to cool moonlit mist */
  uNight: { value: 0 },
};
/** GLSL: cloud-shadow multiplier at a world xz (needs uDetail, uTime, uCloudShadow). */
export const CLOUD_SHADOW_GLSL = /* glsl */ `
float cloudShade( vec2 xz ) {
  float c = texture2D( uDetail, ( xz + uTime * vec2( 1.3, 0.55 ) ) * 0.0068 ).r * 0.7 + texture2D( uDetail, ( xz + uTime * vec2( 0.9, 0.8 ) ) * 0.0029 + 0.4 ).r * 0.3;
  c = smoothstep( 0.42, 0.58, c );
  return 1.0 - c * uCloudShadow;
}
`;

/** GLSL (needs uNight): the meadow's gold albedo goes cool blue-green under the moon. */
export const NIGHT_MEADOW_GLSL = 'diffuseColor.rgb = mix( diffuseColor.rgb, dot( diffuseColor.rgb, vec3( 0.3, 0.59, 0.11 ) ) * vec3( 0.16, 0.62, 0.66 ) + diffuseColor.rgb * 0.18, uNight * 0.55 ) * ( 1.0 - 0.35 * uNight );';

/** The two moods the shared haze blends between (see setMood). */
export const MOOD = {
  meadow: { density: 0.0078, low: 0.75, color: new THREE.Color(0xf8dba6), lowColor: new THREE.Color(0xf8e2b8) },
  town: { density: 0.0036, low: 0.3, color: new THREE.Color(0xf1dcb4), lowColor: new THREE.Color(0xf0d6a8) },
};
/** Sun direction per mood: the meadow keeps its low golden sun (~25 degrees); in town the same
 *  azimuth rises to ~41 degrees so building shadows cover about a third of the street, not all of it. */
const SUN_MEADOW = new THREE.Vector3(-0.55, 0.42, -0.72).normalize();
const SUN_TOWN = new THREE.Vector3(-0.55, 0.8, -0.72).normalize();
/** Blend the shared haze uniforms toward the town mood (0..1). Allocation-free; costs nothing per material. */
export function setMood(v: number) {
  const m = MOOD.meadow, t = MOOD.town;
  HAZE.uTownMood.value = v;
  // the stage's shadow-casting sun reads this same vector (followShadow / WorldView.update)
  (HAZE.uSunDir.value as THREE.Vector3).lerpVectors(SUN_MEADOW, SUN_TOWN, v).normalize();
  HAZE.uCloudShadow.value = 0.56 * (1 - v);
  HAZE.uHazeDensity.value = m.density + (t.density - m.density) * v;
  HAZE.uHazeLow.value = m.low + (t.low - m.low) * v;
  (HAZE.uHazeColor.value as THREE.Color).lerpColors(m.color, t.color, v);
  (HAZE.uLowHazeColor.value as THREE.Color).lerpColors(m.lowColor, t.lowColor, v);
}

const gradientCache = new Map<number, THREE.DataTexture>();
/** 2 hard tones for actors and props, 3 for terrain. */
export function bandRamp(bands: 2 | 3 | 4) {
  let t = gradientCache.get(bands);
  if (t) return t;
  const vals = bands === 2 ? [110, 255] : bands === 3 ? [85, 175, 255] : [70, 140, 205, 255];
  const data = new Uint8Array(vals.length * 4);
  vals.forEach((v, i) => data.set([v, v, v, 255], i * 4));
  t = new THREE.DataTexture(data, vals.length, 1, THREE.RGBAFormat);
  t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter; t.generateMipmaps = false;
  t.needsUpdate = true;
  gradientCache.set(bands, t);
  return t;
}

const HAZE_VERT_DECL = /* glsl */ `
varying vec3 vHazeWorld;
`;
const HAZE_VERT = /* glsl */ `
{
  vec4 hzw = vec4( transformed, 1.0 );
  #ifdef USE_INSTANCING
    hzw = instanceMatrix * hzw;
  #endif
  vHazeWorld = ( modelMatrix * hzw ).xyz;
}
`;
const HAZE_FRAG_DECL = /* glsl */ `
varying vec3 vHazeWorld;
uniform vec3 uHazeColor;
uniform vec3 uSunHazeColor;
uniform vec3 uLowHazeColor;
uniform vec3 uSunDir;
uniform float uHazeDensity;
uniform float uHazeBase;
uniform float uHazeHeightFalloff;
uniform float uHazeLow;
uniform float uFogMax;
uniform float uTownMood;
uniform float uNight;
vec3 applyHaze( vec3 col ) {
  vec3 toFrag = vHazeWorld - cameraPosition;
  float dist = length( toFrag );
  float fd = 1.0 - exp( -pow( dist * uHazeDensity, 1.45 ) );
  float hf = exp( -max( vHazeWorld.y - uHazeBase, 0.0 ) * uHazeHeightFalloff );
  float f = fd * ( 0.6 + 0.4 * hf ) + hf * uHazeLow * smoothstep( 25.0, 160.0, dist ) * 0.55;
  // cap: mid-distance surfaces (island tops seen from above, the far town) keep their form
  // instead of washing to flat cream; only the very far distance may go further
  f = clamp( f, 0.0, min( uFogMax, 0.56 + 0.3 * smoothstep( 320.0, 750.0, dist ) ) );
  float sunAmt = pow( max( dot( toFrag / max( dist, 1e-3 ), uSunDir ), 0.0 ), 5.0 );
  vec3 hz = mix( mix( uHazeColor, uLowHazeColor, hf * 0.6 ), uSunHazeColor, sunAmt * 0.8 );
  col = mix( col, hz, f );
  // golden ground mist hugging the meadow floor, strongest 20-80 m out
  float gm = exp( -abs( vHazeWorld.y - 0.8 ) * 0.4 ) * smoothstep( 14.0, 42.0, dist ) * ( 1.0 - smoothstep( 85.0, 170.0, dist ) );
  vec3 mist = mix( mix( vec3( 0.98, 0.7, 0.34 ), uLowHazeColor * 1.5, uNight ), uSunHazeColor, sunAmt * 0.5 );
  return mix( col, mist, min( gm * 0.34 * ( 1.0 - uTownMood ), uFogMax ) );
}
`;

export interface PrintOpts {
  bands?: 2 | 3 | 4;
  color?: THREE.ColorRepresentation;
  vertexColors?: boolean;
  /** cap the haze so actors stay readable (enemies ~0.35) */
  fogMax?: number;
  emissive?: THREE.ColorRepresentation;
  emissiveIntensity?: number;
  side?: THREE.Side;
  transparent?: boolean;
  opacity?: number;
  /** wind sway amplitude for foliage (vertex y-weighted), 0 = none */
  wind?: number;
}

/** Patch any built-in material with the haze. */
export function withHaze<T extends THREE.Material>(mat: T, fogMax = 1, wind = 0): T {
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    prev?.call(mat, shader, renderer);
    Object.assign(shader.uniforms, HAZE, { uFogMax: { value: fogMax } });
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + HAZE_VERT_DECL + (wind ? 'uniform float uTime;\n' : ''))
      .replace('#include <project_vertex>', (wind ? windChunk(wind) : '') + '#include <project_vertex>\n' + HAZE_VERT);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + HAZE_FRAG_DECL)
      .replace('#include <fog_fragment>', 'gl_FragColor.rgb = applyHaze( gl_FragColor.rgb );');
  };
  mat.customProgramCacheKey = () => `haze${fogMax}_${wind}_${mat.type}`;
  return mat;
}

function windChunk(amp: number) {
  return /* glsl */ `
  {
    vec4 wp = vec4( transformed, 1.0 );
    #ifdef USE_INSTANCING
      wp = instanceMatrix * wp;
    #endif
    wp = modelMatrix * wp;
    float h = max( position.y, 0.0 );
    float sway = sin( uTime * 1.7 + wp.x * 0.21 + wp.z * 0.13 ) * 0.6 + sin( uTime * 3.1 + wp.x * 0.7 ) * 0.25;
    transformed.x += sway * h * ${amp.toFixed(3)};
    transformed.z += cos( uTime * 1.3 + wp.z * 0.19 ) * h * ${(amp * 0.6).toFixed(3)};
  }
  `;
}

/** The workhorse: flat toon + haze. */
export function printMaterial(o: PrintOpts = {}) {
  const m = new THREE.MeshToonMaterial({
    color: o.color ?? 0xffffff,
    vertexColors: o.vertexColors ?? true,
    gradientMap: bandRamp(o.bands ?? 2),
    emissive: o.emissive ?? 0x000000,
    emissiveIntensity: o.emissiveIntensity ?? 1,
    side: o.side ?? THREE.FrontSide,
    transparent: o.transparent ?? false,
    opacity: o.opacity ?? 1,
  });
  return withHaze(m, o.fogMax ?? 1, o.wind ?? 0);
}

export interface PaintedOpts {
  color?: THREE.ColorRepresentation;
  vertexColors?: boolean;
  map?: THREE.Texture | null;
  /** grey stroke texture sampled in world space (xz) to break up flat colour; 0.5 = neutral */
  detail?: THREE.Texture | null;
  /** world metres per detail tile (two octaves are taken: this and ~4.3x larger) */
  detailScale?: number;
  /** how hard the detail modulates the colour */
  detailAmt?: number;
  /** sample detail on the side axes too (rocks, cliffs) instead of xz only */
  triplanar?: boolean;
  /** drifting cloud shadows (meadow ground) */
  cloudShadow?: boolean;
  alphaTest?: number;
  side?: THREE.Side;
  /** dither out within ~2.5 m of the camera (canopies and shrubs the boom can't collide with) */
  nearFade?: boolean;
  fogMax?: number;
  wind?: number;
  emissive?: THREE.ColorRepresentation;
  /** rock treatment (needs triplanar detail): 'strata' = sedimentary value bands in world y with a
   *  lit top edge and dark undercut per band, downward faces near-black (island underside);
   *  'stone' = moss on up-facing faces, lichen blotches, rain streaks, darker lower faces (props) */
  rock?: 'strata' | 'stone';
}

/**
 * Soft painted surface (round 2 landscape): smooth Lambert light instead of hard print bands,
 * with an optional world-space stroke texture so large areas (ground, rock, cobble) read as
 * brushwork rather than flat plastic. Haze as everywhere else.
 */
// Island underside: horizontal sedimentary bands (warped in y so they wander like real strata),
// each with a lit top edge and a dark undercut; the vertex colour carries the tier envelope
// (bright lip, dark seams, deeper tiers darker) as a ratio against the plain side colour.
const STRATA_GLSL = /* glsl */ `
          {
            // the shading normal is the welded vertex normal (smooth rounded rock). No screen-space
            // derivative normal here: on long sliver triangles it misfires along the edges and
            // draws dotted seams
            vec3 sn = normalize( inverseTransformDirection( normalize( vNormal ), viewMatrix ) );
            sn *= gl_FrontFacing ? 1.0 : -1.0; // (a sign test against the face normal dotted the seams)
            // low-frequency warp only (a high-frequency one dithers/aliases the band edges)
            float wlf = texture2D( uDetail, vHazeWorld.xz * 0.021 + 0.13 ).r;
            float yy = ( vHazeWorld.y + ( wlf - 0.5 ) * 3.5 + sin( vHazeWorld.x * 0.071 + vHazeWorld.z * 0.053 ) * 1.4 ) / 2.3;
            float bid = floor( yy ), bf = fract( yy );
            float bh = fract( sin( bid * 12.9898 + 4.1 ) * 43758.5453 ), bh0 = fract( sin( ( bid - 1.0 ) * 12.9898 + 4.1 ) * 43758.5453 );
            vec3 sc = bh < 0.3 ? vec3( 0.26, 0.16, 0.09 ) : bh < 0.52 ? vec3( 0.07, 0.055, 0.045 ) : bh < 0.8 ? vec3( 0.4, 0.28, 0.16 ) : vec3( 0.56, 0.37, 0.19 );
            vec3 sc0 = bh0 < 0.3 ? vec3( 0.26, 0.16, 0.09 ) : bh0 < 0.52 ? vec3( 0.07, 0.055, 0.045 ) : bh0 < 0.8 ? vec3( 0.4, 0.28, 0.16 ) : vec3( 0.56, 0.37, 0.19 );
            // anti-aliased band edge: blend from the band below across about one pixel
            float bsh = mix( 0.32, 1.0, smoothstep( 0.0, 0.2, bf ) ) * ( 1.0 + 0.4 * smoothstep( 0.8, 0.96, bf ) );
            sc = mix( sc0 * 1.4, sc * bsh, smoothstep( 0.0, max( fwidth( yy ) * 1.5, 1e-3 ), bf ) );
            float vl = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
            sc *= clamp( vl / 0.046, 0.06, 1.7 ) * ( 1.0 + dd * 0.9 );
            // undersides fall into shadow, lifted by a warm bounce off the sunlit ground below
            float dn = smoothstep( -0.2, -0.6, sn.y );
            // bands cut across a near-flat underside contour into blotches: fade them to one
            // averaged band tone there, keeping only the soft detail grain
            sc = mix( sc, vec3( 0.3, 0.2, 0.11 ) * clamp( vl / 0.046, 0.06, 1.7 ) * ( 1.0 + dd * 0.9 ), dn * 0.8 );
            sc = mix( sc, sc * 0.62 + vec3( 0.075, 0.048, 0.024 ), dn );
            // vertical joints and spalling on the walls: thin dark cracks running down the bands
            // (a noise stretched in y, thresholded to a hairline), with paler spalled flakes
            float wall = 1.0 - smoothstep( 0.35, 0.7, abs( sn.y ) );
            float ck = texture2D( uDetail, vec2( ( vHazeWorld.x * 0.8 + vHazeWorld.z * 0.6 ) * 0.11, vHazeWorld.y * 0.005 ) ).r;
            float crack = 1.0 - smoothstep( 0.0, max( fwidth( ck ) * 1.2, 0.006 ), abs( ck - 0.5 ) );
            float sp = smoothstep( 0.66, 0.72, texture2D( uDetail, vHazeWorld.xy * 0.19 + vHazeWorld.zy * 0.17 + 0.6 ).r );
            sc *= 1.0 - wall * ( 0.32 * crack - 0.12 * sp );
            // key-light modelling on the smooth normal: rounded mass, not cut facets
            sc *= 0.68 + 0.55 * max( dot( sn, normalize( vec3( -0.55, 0.25, -0.72 ) ) ), 0.0 ) + 0.12 * sn.x;
            sc *= 1.0 + 0.5 * smoothstep( 0.45, 0.8, sn.y );
            // the grass skirt at the island rim keeps its own colour
            sc = mix( diffuseColor.rgb, sc, smoothstep( -0.004, 0.012, diffuseColor.r - diffuseColor.g ) );
            diffuseColor.rgb = sc;
            // the warm bounce off the sunlit ground as light, not albedo: a shadowed, backlit
            // underside otherwise renders as a flat black disc whatever its paint (day only)
            totalEmissiveRadiance += sc * dn * 0.3 * ( 1.0 - uNight );
          }`;
// Standing stones, boulders and ruins: moss on the tops, pale lichen blotches, dark rain streaks
// down the faces, all strong enough to read at gameplay distance.
const STONE_GLSL = /* glsl */ `
          {
            vec3 gn = normalize( cross( dFdx( vHazeWorld ), dFdy( vHazeWorld ) ) );
            float mossN = texture2D( uDetail, vHazeWorld.xz * 0.21 + 0.17 ).r;
            // moss in patches on the up-facing crowns (a finer noise breaks it up), never a full lid
            float mossP = texture2D( uDetail, vHazeWorld.xz * 0.83 + 0.41 ).r;
            float moss = smoothstep( 0.45, 0.85, gn.y + ( mossN - 0.5 ) * 0.7 ) * smoothstep( 0.3, 0.8, mossP ); // soft, no stencil edges
            float streak = texture2D( uDetail, vec2( ( vHazeWorld.x + vHazeWorld.z ) * 1.7, vHazeWorld.y * 0.06 ) ).r;
            float side = 1.0 - smoothstep( 0.3, 0.6, abs( gn.y ) );
            diffuseColor.rgb *= 1.0 - side * smoothstep( 0.5, 0.75, streak ) * 0.5;
            float lich = smoothstep( 0.55, 0.8, texture2D( uDetail, vHazeWorld.xy * 0.37 + vHazeWorld.zy * 0.29 ).r ) * 0.6;
            diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.62, 0.6, 0.42 ), lich * 0.55 * ( 1.0 - moss ) );
            vec3 mossC = mix( vec3( 0.13, 0.2, 0.025 ), vec3( 0.32, 0.36, 0.05 ), mossN );
            diffuseColor.rgb = mix( diffuseColor.rgb, mossC, moss * 0.75 );
          }`;

export function paintedMaterial(o: PaintedOpts = {}) {
  const m = new THREE.MeshLambertMaterial({
    color: o.color ?? 0xffffff,
    vertexColors: o.vertexColors ?? true,
    map: o.map ?? null,
    alphaTest: o.alphaTest ?? 0,
    side: o.side ?? THREE.FrontSide,
    emissive: o.emissive ?? 0x000000,
  });
  const det = o.detail ?? null;
  if (det) {
    const u = {
      uDetail: { value: det },
      uDetailScale: { value: 1 / (o.detailScale ?? 3) },
      uDetailAmt: { value: o.detailAmt ?? 0.5 },
    };
    const tri = !!o.triplanar;
    m.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, u, { uTime: HAZE.uTime, uCloudShadow: HAZE.uCloudShadow });
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D uDetail; uniform float uDetailScale; uniform float uDetailAmt; uniform float uTime; uniform float uCloudShadow;\n' + (o.cloudShadow ? CLOUD_SHADOW_GLSL : ''))
        .replace('#include <color_fragment>', `#include <color_fragment>
        {
          vec3 wp = vHazeWorld * uDetailScale;
          ${tri ? `vec3 an = abs( ${o.rock === 'strata' ? 'inverseTransformDirection( normalize( vNormal ), viewMatrix )' : 'normalize( cross( dFdx( vHazeWorld ), dFdy( vHazeWorld ) ) )'} );
          an = pow( an, vec3( 3.0 ) ); an /= an.x + an.y + an.z;
          float d1 = texture2D( uDetail, wp.zy ).r * an.x + texture2D( uDetail, wp.xz ).r * an.y + texture2D( uDetail, wp.xy ).r * an.z;
          float d2 = texture2D( uDetail, wp.zy * 0.23 + 0.31 ).r * an.x + texture2D( uDetail, wp.xz * 0.23 + 0.31 ).r * an.y + texture2D( uDetail, wp.xy * 0.23 + 0.31 ).r * an.z;`
          : `float d1 = texture2D( uDetail, wp.xz ).r;
          float d2 = texture2D( uDetail, wp.xz * 0.23 + 0.31 ).r;`}
          float dd = ( d1 - 0.5 ) * 0.65 + ( d2 - 0.5 ) * 0.6;
          ${o.cloudShadow ? `// meadow ground: long wind-combed grass strokes (stretched along one axis) so the
          // 15-40 m band reads as painted grass, not flat colour
          float st = texture2D( uDetail, vec2( vHazeWorld.x * 0.9 + vHazeWorld.z * 0.35, vHazeWorld.z * 0.09 - vHazeWorld.x * 0.03 ) ).r;
          float st2 = texture2D( uDetail, vec2( vHazeWorld.x * 0.37 - vHazeWorld.z * 0.12, vHazeWorld.z * 0.045 ) + 0.5 ).r;
          dd += ( st - 0.5 ) * 0.55 + ( st2 - 0.5 ) * 0.4;
          // near field: a dense short-grass undercoat (two crossed fine stroke layers, darker in
          // the gaps) so the ground between the blades reads as grass, not a flat orange floor
          float sgA = texture2D( uDetail, vec2( vHazeWorld.x * 3.4 + vHazeWorld.z * 0.6, vHazeWorld.z * 0.8 ) ).r;
          float sgB = texture2D( uDetail, vec2( vHazeWorld.z * 2.9 - vHazeWorld.x * 0.5, vHazeWorld.x * 0.7 ) + 0.37 ).r;
          float sgN = 1.0 - smoothstep( 14.0, 50.0, distance( vHazeWorld, cameraPosition ) );
          diffuseColor.rgb *= mix( 0.9, 0.58 + 0.62 * smoothstep( 0.3, 0.75, max( sgA, sgB ) ), sgN );
          // past the blades the turf goes soft and low-frequency (broad painted swells, no
          // pixel-scale noise), and from far / above it reads as one golden grass carpet
          float gfar = smoothstep( 12.0, 45.0, distance( vHazeWorld, cameraPosition ) );
          float lfA = texture2D( uDetail, vHazeWorld.xz * 0.013 + 0.21 ).r, lfB = texture2D( uDetail, vHazeWorld.xz * 0.0047 + 0.63 ).r;
          dd = mix( dd, ( lfA - 0.5 ) * 0.55 + ( lfB - 0.5 ) * 0.45, gfar * 0.85 );
          vec3 carpet = mix( vec3( 0.74, 0.37, 0.05 ), vec3( 0.86, 0.5, 0.1 ), lfA ) * mix( 0.85, 1.08, lfB );
          diffuseColor.rgb = mix( diffuseColor.rgb, carpet, smoothstep( 25.0, 110.0, distance( vHazeWorld, cameraPosition ) ) * 0.7 );` : ''}
          diffuseColor.rgb *= 1.0 + dd * uDetailAmt * 2.0;
          ${o.rock === 'strata' ? STRATA_GLSL : o.rock === 'stone' ? STONE_GLSL : ''}
          ${o.cloudShadow ? 'diffuseColor.rgb *= mix( vec3( 1.0 ), vec3( 0.86, 0.94, 1.02 ), 1.0 - cloudShade( vHazeWorld.xz ) ) * cloudShade( vHazeWorld.xz );' : ''}
          ${o.cloudShadow ? '// night: the golden meadow turns moonlit blue-green (daynight.ts)\n          ' + NIGHT_MEADOW_GLSL : ''}
        }`);
    };
  }
  withHaze(m, o.fogMax ?? 1, o.wind ?? 0);
  if (o.nearFade) {
    const hazed = m.onBeforeCompile;
    m.onBeforeCompile = (shader, renderer) => {
      hazed.call(m, shader, renderer);
      shader.uniforms.uFocus = HAZE.uFocus;
      shader.fragmentShader = shader.fragmentShader.replace('#include <common>', '#include <common>\nuniform vec3 uFocus;').replace('#include <alphatest_fragment>', `#include <alphatest_fragment>
        {
          // screen-door fade so leaves between the camera and the cowboy never fill the frame
          float nd = distance( vHazeWorld, cameraPosition );
          float keep = smoothstep( 1.0, 2.6, nd );
          // and anything in the sight line to the cowboy (a shrub 3-6 m out would hide them)
          vec3 sl = uFocus - cameraPosition;
          float sll = dot( sl, sl );
          if ( sll > 1.0 ) {
            float st = clamp( dot( vHazeWorld - cameraPosition, sl ) / sll, 0.0, 1.0 );
            float sd = distance( vHazeWorld, cameraPosition + sl * st );
            keep = min( keep, mix( 1.0, smoothstep( 0.7, 1.6, sd ), step( st, 0.9 ) ) );
          }
          if ( keep < 0.999 && fract( 52.9829189 * fract( dot( gl_FragCoord.xy, vec2( 0.06711056, 0.00583715 ) ) ) ) >= keep ) discard;
        }`);
    };
  }
  const key = `painted${o.rock ?? ''}${det ? (o.triplanar ? 'T' : 'D') : ''}${o.cloudShadow ? 'C' : ''}${o.nearFade ? 'N' : ''}_${o.fogMax ?? 1}_${o.wind ?? 0}`;
  m.customProgramCacheKey = () => key;
  return m;
}

/** Unlit + haze: skies, glows, baked things. */
export function unlitMaterial(o: PrintOpts = {}) {
  const m = new THREE.MeshBasicMaterial({
    color: o.color ?? 0xffffff, vertexColors: o.vertexColors ?? false,
    side: o.side ?? THREE.FrontSide, transparent: o.transparent ?? false, opacity: o.opacity ?? 1,
  });
  return withHaze(m, o.fogMax ?? 1, o.wind ?? 0);
}

/**
 * Inverted-hull ink outline. Works on plain and instanced meshes: render the same geometry
 * with this material (back faces, pushed out along the normal). Characters and enemies only.
 */
export function outlineMaterial(width = 0.035, color: THREE.ColorRepresentation = PAL.inkDark) {
  const m = new THREE.MeshBasicMaterial({ color, side: THREE.BackSide });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uOutline = { value: width };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uOutline;')
      .replace('#include <begin_vertex>', /* glsl */ `
        vec3 transformed = vec3( position );
        // slightly wobbly width reads as a print edge, not a CG outline
        float wob = 0.8 + 0.4 * fract( sin( dot( position.xyz, vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 );
        transformed += normalize( normal ) * uOutline * wob;
      `);
  };
  m.customProgramCacheKey = () => `outline${width}`;
  return m;
}

/** Bake a solid colour into a geometry's vertex colours (for merging many parts into one mesh). */
export function paint(geo: THREE.BufferGeometry, color: THREE.ColorRepresentation, jitter = 0, seed = 1) {
  const c = new THREE.Color(color);
  const n = geo.attributes.position.count;
  const arr = new Float32Array(n * 3);
  let s = seed;
  for (let i = 0; i < n; i++) {
    let k = 1;
    if (jitter) { s = (s * 16807) % 2147483647; k = 1 + ((s / 2147483647) - 0.5) * jitter; }
    arr[i * 3] = c.r * k; arr[i * 3 + 1] = c.g * k; arr[i * 3 + 2] = c.b * k;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return geo;
}

/** Faceted low-poly look: MeshToonMaterial has no flatShading, so split faces and use face normals. */
export function facet(geo: THREE.BufferGeometry) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  g.deleteAttribute('normal');
  g.computeVertexNormals();
  return g;
}
